import { Check, Pause } from 'lucide-react'
import { formatScorecardStatusLabel } from '@/features/scorecard/scorecard-status'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { OgsmStatus } from '@/types/api'

/**
 * A ring around a pastel disc; `bg-clip-content` plus the padding leaves a thin
 * gap between the two, as in the design. On Hold is a ring and glyph, no disc.
 */
const RING = 'border-[1.5px]'

const DOT: Record<OgsmStatus, string> = {
  ON_TRACK: 'border-status-on-track-ring bg-status-on-track',
  ACHIEVED: 'border-status-achieved bg-status-achieved text-white',
  AT_RISK: 'border-status-at-risk-ring bg-status-at-risk',
  OFF_TRACK: 'border-status-off-track-ring bg-status-off-track',
  NOT_STARTED: 'border-status-not-started-ring bg-status-not-started',
  ON_HOLD: 'border-status-not-started-ring text-status-not-started-ring',
}

/** The legend shows the bare disc, with no ring; On Hold keeps its ring as it has no disc. */
const PLAIN_DOT: Record<OgsmStatus, string> = {
  ON_TRACK: 'bg-status-on-track',
  ACHIEVED: 'bg-status-achieved text-white',
  AT_RISK: 'bg-status-at-risk',
  OFF_TRACK: 'bg-status-off-track',
  NOT_STARTED: 'bg-status-not-started',
  ON_HOLD: `${RING} border-status-not-started-ring text-status-not-started-ring`,
}

const LEGEND_ORDER: OgsmStatus[] = [
  'ON_TRACK',
  'AT_RISK',
  'OFF_TRACK',
  'NOT_STARTED',
  'ON_HOLD',
  'ACHIEVED',
]

/**
 * `decorative` hides the mark from assistive tech, for places where the
 * status is already named next to it (the status picker's options).
 */
export function StatusMark({
  status,
  decorative = false,
  plain = false,
}: {
  status: OgsmStatus
  decorative?: boolean
  plain?: boolean
}) {
  return (
    <span
      {...(decorative
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': formatScorecardStatusLabel(status) })}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full',
        plain
          ? ['size-3.5', PLAIN_DOT[status]]
          : ['size-4.5 bg-clip-content p-0.5', RING, DOT[status]]
      )}
    >
      {status === 'ACHIEVED' ? (
        <Check className="size-2.5" aria-hidden />
      ) : null}
      {status === 'ON_HOLD' ? (
        <Pause
          className={plain ? 'size-2' : 'size-2.5'}
          fill="currentColor"
          aria-hidden
        />
      ) : null}
    </span>
  )
}

export function StatusLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
      {LEGEND_ORDER.map((status) => (
        <span key={status} className="inline-flex items-center gap-2">
          <StatusMark status={status} plain />
          <Typography as="span" variant="body-sm" className="text-primary/60">
            {formatScorecardStatusLabel(status)}
          </Typography>
        </span>
      ))}
    </div>
  )
}
